/**
 * EmailLogsTab.tsx - Email Logs tab for Admin Dashboard
 *
 * Displays email send history with open/click tracking, filtering by
 * email type and event status.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { authFetch } from '../../services/apiAuth';
import { getApiBaseUrl } from '../../utils/apiConfig';
import './EmailLogsTab.css';

interface EmailLogEntry {
  id: string;
  eventType: string;
  userId: string;
  emailType: string;
  timestamp: string;
  environment: string;
  details: {
    to?: string;
    subject?: string;
  };
  tracking?: {
    opened?: boolean;
    openedAt?: string;
    openCount?: number;
    clicked?: boolean;
    clickedAt?: string;
    clickCount?: number;
  };
  result?: {
    provider?: string;
    sentAt?: string;
    error?: string;
  };
}

interface EmailLogsTabProps {
  language: string;
}

export default function EmailLogsTab({ language: _language }: EmailLogsTabProps) {
  const { t } = useTranslation('admin');
  const [logs, setLogs] = useState<EmailLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [emailTypeFilter, setEmailTypeFilter] = useState('');
  const [eventTypeFilter, setEventTypeFilter] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [stats, setStats] = useState({ total: 0, sent: 0, failed: 0, opened: 0, clicked: 0 });

  const loadLogs = useCallback(async (cursor?: string) => {
    if (cursor) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    setError(null);

    try {
      const params = new URLSearchParams({ limit: '50' });
      if (cursor) params.set('cursor', cursor);
      if (emailTypeFilter) params.set('emailType', emailTypeFilter);
      if (eventTypeFilter) params.set('eventType', eventTypeFilter);

      const response = await authFetch(`${getApiBaseUrl()}/getEmailLogs?${params.toString()}`);
      if (!response.ok) {
        setError('Failed to load email logs');
        return;
      }

      const result = await response.json();
      if (result.success) {
        if (cursor) {
          setLogs(prev => [...prev, ...result.data.logs]);
        } else {
          setLogs(result.data.logs);
        }
        setNextCursor(result.data.nextCursor);
        if (result.data.stats) setStats(result.data.stats);
      } else {
        setError('Failed to load email logs');
      }
    } catch (err) {
      console.error('Error loading email logs:', err);
      setError('Failed to load email logs');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [emailTypeFilter, eventTypeFilter]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const formatTimestamp = (ts: string) => {
    if (!ts) return '—';
    try {
      return new Date(ts).toLocaleDateString(undefined, {
        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
      });
    } catch { return ts; }
  };

  const getEventBadge = (eventType: string) => {
    const map: Record<string, { labelKey: string; className: string }> = {
      'success': { labelKey: 'emailLogs.badges.sent', className: 'badge-success' },
      'failure': { labelKey: 'emailLogs.badges.failed', className: 'badge-failure' },
      'attempt': { labelKey: 'emailLogs.badges.attempt', className: 'badge-attempt' },
      'unsubscribe': { labelKey: 'emailLogs.badges.unsub', className: 'badge-unsub' },
    };
    const info = map[eventType] || { labelKey: eventType, className: 'badge-default' };
    return <span className={`email-badge ${info.className}`}>{t(info.labelKey, info.labelKey)}</span>;
  };

  const getEmailTypeLabel = (type: string) => {
    const map: Record<string, string> = {
      'sample-shipped': 'emailLogs.emailTypes.sampleShipped',
      'sample-tips': 'emailLogs.emailTypes.sampleTips',
      'sample-feedback': 'emailLogs.emailTypes.sampleFeedback',
      'sample-discount': 'emailLogs.emailTypes.sampleDiscount',
      'reorder-reminder': 'emailLogs.emailTypes.reorderReminder',
    };
    return t(map[type] || type, type);
  };

  // Hide redundant "attempt" entries when a "success" exists for the same recipient + email type
  const displayLogs = useMemo(() => {
    const successKeys = new Set(
      logs
        .filter(l => l.eventType === 'success')
        .map(l => `${l.details.to}|${l.emailType}`)
    );
    return logs.filter(l => l.eventType !== 'attempt' || !successKeys.has(`${l.details.to}|${l.emailType}`));
  }, [logs]);

  if (loading) {
    return (
      <div className="email-logs-loading">
        <div className="loading-spinner" />
        <p>{t('emailLogs.loading', 'Loading email logs...')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="email-logs-error">
        <p>{error}</p>
        <button onClick={() => loadLogs()} className="retry-btn">
          {t('emailLogs.retry', 'Retry')}
        </button>
      </div>
    );
  }

  return (
    <div className="email-logs-tab">
      <div className="email-logs-header">
        <h3>📧 {t('emailLogs.title', 'Email Logs')}</h3>
        <p className="email-logs-subtitle">
          {t('emailLogs.subtitle', 'Track sent emails, opens, clicks, and delivery status')}
        </p>
      </div>

      {/* Stats Bar */}
      <div className="email-stats-bar">
        <div className="email-stat">
          <span className="email-stat-value">{stats.total}</span>
          <span className="email-stat-label">{t('emailLogs.stats.loaded', 'Loaded')}</span>
        </div>
        <div className="email-stat">
          <span className="email-stat-value">{stats.sent}</span>
          <span className="email-stat-label">{t('emailLogs.stats.sent', 'Sent')}</span>
        </div>
        <div className="email-stat">
          <span className="email-stat-value">{stats.failed}</span>
          <span className="email-stat-label">{t('emailLogs.stats.failed', 'Failed')}</span>
        </div>
        <div className="email-stat">
          <span className="email-stat-value">{stats.opened}</span>
          <span className="email-stat-label">{t('emailLogs.stats.opened', 'Opened')}</span>
        </div>
        <div className="email-stat">
          <span className="email-stat-value">{stats.clicked}</span>
          <span className="email-stat-label">{t('emailLogs.stats.clicked', 'Clicked')}</span>
        </div>
      </div>

      {/* Filters */}
      <div className="email-filters">
        <select
          value={emailTypeFilter}
          onChange={(e) => setEmailTypeFilter(e.target.value)}
          className="email-filter-select"
        >
          <option value="">{t('emailLogs.filters.allEmailTypes', 'All Email Types')}</option>
          <option value="sample-shipped">{t('emailLogs.filters.sampleShipped', 'Sample Shipped')}</option>
          <option value="sample-tips">{t('emailLogs.filters.sampleTips', 'Sample Tips')}</option>
          <option value="sample-feedback">{t('emailLogs.filters.feedbackRequest', 'Feedback Request')}</option>
          <option value="sample-discount">{t('emailLogs.filters.discountCode', 'Discount Code')}</option>
          <option value="reorder-reminder">{t('emailLogs.filters.reorderReminder', 'Reorder Reminder')}</option>
        </select>
        <select
          value={eventTypeFilter}
          onChange={(e) => setEventTypeFilter(e.target.value)}
          className="email-filter-select"
        >
          <option value="">{t('emailLogs.filters.allStatuses', 'All Statuses')}</option>
          <option value="success">{t('emailLogs.filters.sent', 'Sent')}</option>
          <option value="failure">{t('emailLogs.filters.failed', 'Failed')}</option>
          <option value="attempt">{t('emailLogs.filters.attempt', 'Attempt')}</option>
          <option value="unsubscribe">{t('emailLogs.filters.unsubscribed', 'Unsubscribed')}</option>
        </select>
      </div>

      {/* Logs List */}
      {displayLogs.length === 0 ? (
        <div className="email-logs-empty">
          <p>📭 {t('emailLogs.empty', 'No email logs found')}</p>
        </div>
      ) : (
        <div className="email-logs-list">
          {displayLogs.map((log) => (
            <div
              key={log.id}
              className={`email-log-card ${expandedId === log.id ? 'expanded' : ''}`}
            >
              <button
                className="email-log-header"
                onClick={() => setExpandedId(prev => prev === log.id ? null : log.id)}
                aria-expanded={expandedId === log.id}
              >
                <div className="email-log-meta">
                  {getEventBadge(log.eventType)}
                  <span className="email-log-type">{getEmailTypeLabel(log.emailType)}</span>
                  <span className="email-log-time">{formatTimestamp(log.timestamp)}</span>
                </div>
                <div className="email-log-summary">
                  <span className="email-log-to">{log.details.to || '—'}</span>
                  {log.tracking?.opened && <span className="email-tracking-icon" title="Opened">👁️</span>}
                  {log.tracking?.clicked && <span className="email-tracking-icon" title="Clicked">🖱️</span>}
                </div>
                <span className="email-log-chevron">
                  {expandedId === log.id ? '▲' : '▼'}
                </span>
              </button>

              {expandedId === log.id && (
                <div className="email-log-details">
                  <div className="email-detail-row">
                    <strong>{t('emailLogs.details.subject', 'Subject')}:</strong> {log.details.subject || '—'}
                  </div>
                  <div className="email-detail-row">
                    <strong>{t('emailLogs.details.to', 'To')}:</strong> {log.details.to || '—'}
                  </div>
                  <div className="email-detail-row">
                    <strong>{t('emailLogs.details.userId', 'User ID')}:</strong> {log.userId || '—'}
                  </div>
                  <div className="email-detail-row">
                    <strong>{t('emailLogs.details.environment', 'Environment')}:</strong> {log.environment || '—'}
                  </div>
                  {log.result && (
                    <>
                      <div className="email-detail-row">
                        <strong>{t('emailLogs.details.provider', 'Provider')}:</strong> {log.result.provider || '—'}
                      </div>
                      {log.result.sentAt && (
                        <div className="email-detail-row">
                          <strong>{t('emailLogs.details.sentAt', 'Sent At')}:</strong> {formatTimestamp(log.result.sentAt)}
                        </div>
                      )}
                      {log.result.error && (
                        <div className="email-detail-row email-detail-error">
                          <strong>{t('emailLogs.details.error', 'Error')}:</strong> {log.result.error}
                        </div>
                      )}
                    </>
                  )}
                  {log.tracking && (
                    <div className="email-tracking-details">
                      <strong>{t('emailLogs.details.tracking', 'Tracking')}:</strong>
                      <div className="email-tracking-grid">
                        <span>{t('emailLogs.details.opened', 'Opened')}: {log.tracking.opened ? `✅ (${log.tracking.openCount || 1}×)` : '—'}</span>
                        {log.tracking.openedAt && <span>{t('emailLogs.details.firstOpen', 'First open')}: {formatTimestamp(log.tracking.openedAt)}</span>}
                        <span>{t('emailLogs.details.clicked', 'Clicked')}: {log.tracking.clicked ? `✅ (${log.tracking.clickCount || 1}×)` : '—'}</span>
                        {log.tracking.clickedAt && <span>{t('emailLogs.details.firstClick', 'First click')}: {formatTimestamp(log.tracking.clickedAt)}</span>}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Load More */}
      {nextCursor && (
        <div className="email-logs-load-more">
          <button
            onClick={() => loadLogs(nextCursor)}
            disabled={loadingMore}
            className="load-more-btn"
          >
            {loadingMore ? t('emailLogs.loadingMore', 'Loading...') : t('emailLogs.loadMore', 'Load More')}
          </button>
        </div>
      )}
    </div>
  );
}
